import React, { useState, useRef } from "react";
import { Button } from "@speedoc/design-assets";
import { useMediaQuery } from "react-responsive";
import ContainerCard from "./containerCard";
import { SimpleTabs } from "./tabs";
import SectionHeading from "./sectionHeading";
import { Remarkable } from "remarkable";

const markdown = new Remarkable();
const tabs = ["Package Details", "Schedule"];

const ExpandedCardBottomInfo = ({ content }) => {
  const { linkText, currency, price, duration, inclusion = [] } = content;
  const services = inclusion.map(({ text }) => text);

  const scrollToForm = () => {
    let form;
    try {
      form = document.querySelector("#contactFormChronic");
      const topPoint = form.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ behavior: "smooth", left: 0, top: topPoint });
    } catch {
      return;
    }
  };

  return (
    <div className="expandedCardBottomInfo">
      {content.price && (
        <p className="priceInfo">
          <span className="currency">{currency}</span>
          <span className="amount"> {price}</span>
          <span className="year"> {duration}</span>
        </p>
      )}
      <div className="packageDetailsList">
        <p>{services && services[0]}</p>
        <div className="horizontalList">
          {services.slice(1, -1).map(detail => {
            return <p key={detail}>{detail}</p>;
          })}
        </div>
        <p>{services[services.length - 1]}</p>
      </div>
      <div className="cardButton">
        <Button type="button" onClick={scrollToForm} className="primary sm">
          <span>{linkText}</span>
        </Button>
      </div>
    </div>
  );
};

const PackageDetails = ({ content, fade }) => {
  return (
    <div className={`packageDetails ${fade ? "fadeContent" : ""}`}>
      {content.map((c, i) => {
        return <div key={i} className="serviceListItem" dangerouslySetInnerHTML={{ __html: markdown.render(c) }} />;
      })}
    </div>
  );
};

const ScheduleHorizontal = ({ content, fade }) => {
  return (
    <div className={`scheduleHorizontal ${fade ? "fadeContent" : ""}`}>
      <div className="timeline">
        <div />
      </div>
      <div className="boarderTop" />
      <div className="calendar">
        {content.map(({ month, treatments }, index) => {
          return (
            <div key={index} className="timeSpanBox" style={{ width: `${100 / content.length}%` }}>
              {treatments.map(({ text }, i) => (
                <p key={i} className="serviceType">
                  {text}
                </p>
              ))}
              <div className="bulletPointBox">
                <p className={`time ${month && month.length ? "" : "hidden"}`}>+{month} mo</p>
                <p className="label"> Visit {index + 1}</p>
                <div className="bulletPoint" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ScheduleVertical = ({ content = [], fade }) => {
  const container = useRef();
  const contentLength = content.length - 1;
  const boxHeight = `0 0 ${Math.round(100 / contentLength)}%`;
  return (
    <div ref={container} className={`scheduleVertical ${fade ? "fadeContent" : ""}`}>
      <div className="details">
        <div className="timelineBoxes">
          {content.slice(0, -1).map((info, index) => (
            <div className="period" key={index} style={{ flex: boxHeight }}>
              {info.treatments.map(({ text }, i) => (
                <p key={i}>{text}</p>
              ))}
              <div className="label">
                <div className="bulletPoint" />
                <div className="text">
                  <p className="month">{info?.month ? <span>+{info.month} mo.</span> : <span className="hidden" />}</p>
                  <p className="visit">VISIT {index + 1}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="lastBox">
          <p>{content && content[content.length - 1]?.treatments[0]?.text}</p>
          <div className="label">
            <div className="bulletPoint" />
            <div className="text">
              <p className="month">
                {content[content.length - 1].month !== "" ? (
                  <span>+{content[content.length - 1].month} mo.</span>
                ) : (
                  <span className="hidden">mo.</span>
                )}
              </p>
              <p className="visit">VISIT {content.length}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="timeline" />
      <div className="labels" />
    </div>
  );
};

const StaticPackageDetails = ({ content }) => {
  const { details, schedule, title, subtitle, image = {}, inclusion, caption } = content;
  const [activeTab, setActiveTab] = useState(0);
  const [fade, setFade] = useState(false);

  const fadeContent = value => {
    return new Promise(resolve => {
      setTimeout(() => {
        setFade(!fade);
        resolve();
      }, 200);
    });
  };

  const changeContent = i => {
    setActiveTab(i);
    return new Promise(resolve => {
      setTimeout(resolve, 100);
    });
  };

  const switchTab = async i => {
    if (activeTab === i) return;
    await changeContent(i);
    await fadeContent(true);
  };

  const buttonDetails = tabs.map((tabTitle, index) => {
    return {
      text: tabTitle,
      classes: `secondary sm ${activeTab === index ? "active" : ""}`
    };
  });

  return (
    <div id="staticPackageDetails">
      <SectionHeading title={title} />
      <div className="expandedCardContainer">
        <ContainerCard className="fullCard">
          <h1 className="title">{subtitle}</h1>
          <div className="cardContent">
            {tabs.length ? <SimpleTabs buttonDetails={buttonDetails} onClick={i => switchTab(i)} /> : null}
            {activeTab === 0 ? (
              <PackageDetails content={details.map(detail => detail.content)} fade={fade} />
            ) : (
              <ScheduleHorizontal content={schedule} fade={fade} />
            )}
            <ExpandedCardBottomInfo content={content} services={inclusion} />
          </div>
          <img className="cardImage" src={image?.url} alt={image?.alternativeText || ""} />
        </ContainerCard>
      </div>
      <div className="bottomInfo">{caption}</div>
    </div>
  );
};

const MobileCard = ({ content = {} }) => {
  const { details, schedule, subtitle, image = {} } = content;

  const [fadeBody] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [fade, setFade] = useState(false);

  const fadeContent = value => {
    return new Promise(resolve => {
      setTimeout(() => {
        setFade(!fade);
        resolve();
      }, 200);
    });
  };
  const changeContent = i => {
    setActiveTab(i);
    return new Promise(resolve => {
      setTimeout(resolve, 100);
    });
  };

  const buttonDetails = tabs.map((title, index) => {
    return {
      text: title,
      classes: `secondary sm ${activeTab === index ? "active" : ""}`
    };
  });

  const switchTab = async i => {
    if (activeTab === i) return;
    await changeContent(i);
    await fadeContent(true);
  };

  return (
    <ContainerCard className={`mobileCard ${activeTab === 1 ? "noPaddingBody" : ""}`}>
      <h2 className="title" dangerouslySetInnerHTML={{ __html: subtitle }} />
      <div className={`cardBody ${fadeBody ? "display" : ""}`}>
        <SimpleTabs buttonDetails={buttonDetails} onClick={i => switchTab(i)} />
        {activeTab === 0 ? (
          <PackageDetails content={details.map(detail => detail.content)} fade={fade} />
        ) : (
          <ScheduleVertical content={schedule} fade={fade} />
        )}
        <ExpandedCardBottomInfo content={content} services={details.includesMobile} />
      </div>
      {image?.url ? <img className="cardImage" src={image.url} alt={image.alternativeText} /> : null}
    </ContainerCard>
  );
};

const StaticPackageDetailsMobile = ({ content }) => {
  return (
    <div id="staticPackagesDetailsMobile">
      <SectionHeading title={content.title} />
      <div className="cardsContainer">
        <MobileCard className="card" content={content} />
      </div>
    </div>
  );
};

export default ({ content }) => {
  const isMobile = useMediaQuery({ query: "(max-width: 992px)" });

  if (isMobile) {
    return <StaticPackageDetailsMobile content={content} />;
  } else {
    return <StaticPackageDetails content={content} />;
  }
};
